import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Button, TextField, TextArea, Notification, Popover, ActivityIndicator } from '@bosch/react-frok'

// components
import type { InputAreaProps } from '@/shared/types/inputArea'
import InputAudio from '../InputAudio/InputAudio'
import useSetQuestion from '../InputAudio/useSetQuestion'
import useAudioRecording from '@/hooks/useAudioRecording'
import FeatureOnboardingPopover from './FeatureOnboardingPopover'

// utils
import { setHideDisclaimer } from '@/utils/store/features/application/applicationSlice'
import { Settings } from '@/constants'
import useWindowSize from '@/hooks/useWindowSize'
import { useConfig } from '@/hooks/useConfig'
import { useSpace } from '@/hooks/useSpace'
import { useAuthentication } from '@/hooks/useAuthentication'
import {
    type FilesContract,
    type FilesRouteParams,
    FileUploadError,
    uploadFile,
    fetchFilesContract,
} from '@/utils/api/filesApi'

import './InputArea.scss'

type AttachmentItem = {
    id: string
    file: File
    source: 'capture' | 'upload'
    // Local preview (object URL) for performance
    previewUrl?: string
    // URL returned by backend upload API
    url?: string
    loading: boolean
    error?: string
}

type OnboardingStep = 'camera' | 'mic' | null

const InputArea: React.FC<InputAreaProps> = ({
    text,
    updateText,
    sendRequest,
    onSendPayload,
    waitingForResponse,
    disabledMessage,
    disabled: disabledProp,
    validateTokenAndRehydrate,
    persistedData,
    assistant,
    filterSlot,
}) => {
    const { isMobile } = useWindowSize()
    const { getConfig } = useConfig()
    const { getActiveSpace, getActiveAssistant } = useSpace()
    const { information, getApiToken } = useAuthentication()
    const {
        audioInputProps,
        isRecording: isAudioRecording,
        isTranscribing: isAudioTranscribing,
        toggleRecording,
    } = useAudioRecording()
    const setQuestion = useSetQuestion(text, updateText)
    const [textFocus, setTextFocus] = useState<boolean>(false)
    const textFieldRef = useRef<HTMLInputElement>(null)
    const textAreaRef = useRef<HTMLTextAreaElement>(null)
    const hasMounted = useRef(false)

    const activeSpace = getActiveSpace()
    const activeAssistant = getActiveAssistant()

    const dispatch = useDispatch()
    const { t, i18n } = useTranslation()

    const disabled = disabledProp ?? assistant?.isDisabled
    const uploadEnabled = assistant?.config.features?.input.upload
    const voice = assistant?.config.features?.input.voice

    const [filesContract, setFilesContract] = useState<FilesContract | null>(null)
    const [attachments, setAttachments] = useState<AttachmentItem[]>([])
    const [uploadError, setUploadError] = useState<string | null>(null)
    const [attachmentLimitError, setAttachmentLimitError] = useState(false)
    const [isLiveCaptureOpen, setIsLiveCaptureOpen] = useState(false)
    const [captureError, setCaptureError] = useState<string | null>(null)
    const [streamReady, setStreamReady] = useState(false)
    const videoRef = useRef<HTMLVideoElement>(null)
    const streamRef = useRef<MediaStream | null>(null)
    const [plusWrapperEl, setPlusWrapperEl] = useState<HTMLDivElement | null>(null)
    const [micWrapperEl, setMicWrapperEl] = useState<HTMLDivElement | null>(null)
    const [onboardingStep, setOnboardingStep] = useState<OnboardingStep>(null)
    const uploadInputRef = useRef<HTMLInputElement>(null)

    // When the contract is loaded but enabled=false, hide upload regardless of config flag
    const uploadActive = uploadEnabled && filesContract?.enabled !== false

    const attachmentNudgeKey = assistant?.config.features?.input.attachmentNudgeKey
    const showAttachmentNudge =
        uploadActive &&
        !!attachmentNudgeKey &&
        attachments.length > 0 &&
        !text.trim()
    const attachmentNudgeText = attachmentNudgeKey
        ? t(`chat:input.attachmentNudge.${attachmentNudgeKey}`, { defaultValue: '' })
        : ''
    const composerPlaceholderKey = assistant?.config.features?.input.composerPlaceholderKey
    const composerPlaceholderText = composerPlaceholderKey
        ? t(`chat:input.composerPlaceholder.${composerPlaceholderKey}`, { defaultValue: t('chat:promptPlaceholder') })
        : t('chat:promptPlaceholder')
    const needsArea = text.length > Settings.inputLineBreakNumber || (isMobile && attachments.length > 0)
    const maxFilesPerMessage = filesContract?.max_files_per_message ?? 10
    const acceptedMimePatterns: string[] = filesContract?.accepted_mime_patterns ?? ['image/*']
    const acceptedExtensions: string[] = filesContract?.accepted_extensions ?? []
    // Feed both MIME patterns and explicit extensions to the OS file picker
    const fileInputAccept = [...acceptedMimePatterns, ...acceptedExtensions].join(',') || 'image/*'

    const isExtensionAccepted = (file: File): boolean => {
        if (!acceptedExtensions.length) return true
        const ext = '.' + (file.name.split('.').pop() ?? '').toLowerCase()
        return acceptedExtensions.map(e => e.toLowerCase()).includes(ext)
    }

    const triggerUploadInput = () => uploadInputRef.current?.click()

    const handleChangeTextField = (event: React.ChangeEvent<HTMLInputElement>) => updateText?.(event.target.value)
    const handleChangeTextArea = (event: React.ChangeEvent<HTMLTextAreaElement>) => updateText?.(event.target.value)

    const revokePreview = (previewUrl?: string) => {
        try {
            if (previewUrl) URL.revokeObjectURL(previewUrl)
        } catch { /* noop */ }
    }

    const discardAttachment = (id?: string) => {
        if (id) {
            setAttachments(prev => {
                const toRemove = prev.find(a => a.id === id)
                if (toRemove) revokePreview(toRemove.previewUrl)
                return prev.filter(a => a.id !== id)
            })
        } else {
            setAttachments(prev => {
                prev.forEach(a => revokePreview(a.previewUrl))
                return []
            })
        }
    }

    const getFilesRouteParams = (): FilesRouteParams => ({
        base:      getConfig()?.endpoint?.replace(/\/$/, '') || '',
        space:     activeSpace?.endpoint || activeSpace?.id || '',
        assistant: activeAssistant?.config.endpoint || activeAssistant?.config.oldId || activeAssistant?.config.id || '',
        userId:    information?.userId || '',
    })

    const uploadImage = async (file: File, id: string) => {
        try {
            const token = await getApiToken()
            const { file_url } = await uploadFile(getFilesRouteParams(), file, token)
            setAttachments(curr => curr.map(a => a.id === id ? { ...a, url: file_url, loading: false } : a))
        } catch (_err) {
            const isNoUrl = _err instanceof Error && _err.message.includes('no file_url')
            const apiMsg = _err instanceof FileUploadError ? _err.apiMessage : undefined
            const msg = apiMsg
                ?? (isNoUrl
                    ? t('chat:input.uploadNoUrl', { defaultValue: 'Upload incomplete. Please discard and retry.' })
                    : t('chat:input.uploadError', { defaultValue: 'Upload failed. Please discard and try again.' }))
            setAttachments(curr => curr.map(a => a.id === id ? { ...a, loading: false, error: msg } : a))
            setUploadError(msg)
            setTimeout(() => setUploadError(null), 5000)
        }
    }

    const isTranscribingWithoutText = isAudioTranscribing && !text
    const renderedFilterSlot = typeof filterSlot === 'function' ? filterSlot({ disabled: isAudioTranscribing }) : filterSlot
    const assistantId = activeAssistant?.config?.id ?? activeAssistant?.config?.endpoint
    const onboardingStorageKey = assistantId ? `mma_camera_mic_onboarding_seen_${assistantId}` : ''
    const onboardingEnabled = !!uploadActive

    const audioButtonSymbol = isAudioRecording ? 'checkmark' : 'microphone-classic'
    const [audioButtonDisabled, setAudioButtonDisabled] = useState(false)

    const handleSend = () => {
        if (waitingForResponse || disabled) return
        const trimmed = text.trim()
        if (!trimmed) return
        // Do not allow sending while uploads are pending
        const hasPending = attachments.some(a => a.loading && !a.error)
        if (hasPending) return
        if (onSendPayload) {
            // Only include successfully uploaded attachments (exclude errored ones)
            const urls = attachments.filter(a => a.url && !a.error).map(a => a.url).filter((u): u is string => !!u)
            onSendPayload({
                message: trimmed,
                file_urls: urls,
                preview_urls: urls,
            })
        } else {
            sendRequest()
        }
        updateText('')
        setUploadError(null)
        // Preserve preview object URLs for chat rendering; clear state without revoking
        setAttachments([])
    }

    const handlePromptKey = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
            handleSend()
        }
    }

    useEffect(() => {
        setAudioButtonDisabled(disabled || waitingForResponse)
    }, [disabled, waitingForResponse])

    useEffect(() => {
        if (!hasMounted.current) {
            hasMounted.current = true
            return
        }
        if (!isTranscribingWithoutText) {
            const newComponent: HTMLInputElement | HTMLTextAreaElement | null = needsArea
                ? textAreaRef.current
                : textFieldRef.current

            if (newComponent) {
                newComponent.focus({ preventScroll: true })
                newComponent.selectionStart = newComponent.value.length
                newComponent.selectionEnd = newComponent.value.length
            }
        }
    }, [needsArea, isTranscribingWithoutText])
    
    useEffect(() => {
        dispatch(setHideDisclaimer(true))
    }, [dispatch, textFocus])

    useEffect(() => {
        if (!uploadEnabled || !activeSpace || !activeAssistant) return
        getApiToken().then(token =>
            fetchFilesContract(getFilesRouteParams(), token)
        ).then(contract => {
            if (contract) setFilesContract(contract)
        }).catch(() => { /* fall back to defaults */ })
        // The contract is loaded again only when the upload flag, the space or the assistant changes, not on every
        // render: the space, the assistant and the functions of the hooks are new objects in every render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadEnabled, activeSpace?.id, activeAssistant?.config?.id])

    useEffect(() => {
        setOnboardingStep(null)
        if (!onboardingEnabled || !assistantId || !onboardingStorageKey) return
        const alreadySeen = window.localStorage.getItem(onboardingStorageKey) === '1'
        if (!alreadySeen) {
            setOnboardingStep('camera')
        }
    }, [assistantId, onboardingEnabled, onboardingStorageKey])

    const getUserManualUrl = (language?: string): string => {
    return language === 'de'
        ? 'https://inside-docupedia.bosch.com/confluence/pages/viewpage.action?pageId=6063034260'
        : 'https://inside-docupedia.bosch.com/confluence/pages/viewpage.action?pageId=5850640415'
}

    const handleOnFocus = () => {
        if (isMobile) { 
            setTextFocus(true)
        }
    }
    const handleOnBlur = () => {
        if (isMobile) { 
            setTextFocus(false)
        }
    }

    const showAttachmentLimitError = () => {
        setAttachmentLimitError(true)
        setTimeout(() => setAttachmentLimitError(false), 3000)
    }

    const handleUploadInputChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(ev.target.files || [])
        if (files.length) {
            const rejected = files.filter(f => !isExtensionAccepted(f))
            if (rejected.length) {
                const extList = acceptedExtensions.length
                    ? acceptedExtensions.join(', ')
                    : acceptedMimePatterns.join(', ')
                const msg = t('chat:input.unsupportedFileType', {
                    defaultValue: `File type not supported. Accepted: ${extList}`,
                    extList,
                })
                setUploadError(msg)
                setTimeout(() => setUploadError(null), 5000)
                if (rejected.length === files.length) {
                    ev.target.value = ''
                    return
                }
            }
            const accepted = files.filter(f => isExtensionAccepted(f))
            setAttachments(prev => {
                const room = Math.max(0, maxFilesPerMessage - prev.length)
                if (accepted.length + prev.length > maxFilesPerMessage) showAttachmentLimitError()
                const toAdd = accepted.slice(0, room).map((file) => ({
                    id: `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                    file,
                    source: 'upload' as const,
                    previewUrl: URL.createObjectURL(file),
                    url: undefined,
                    loading: true,
                }))
                // Schedule uploads asynchronously without nesting
                scheduleUploads(toAdd)
                return [...prev, ...toAdd]
            })
        }
        ev.target.value = ''
    }

    const scheduleUploads = (items: AttachmentItem[]) => {
        // run uploads asynchronously outside of state setter
        setTimeout(() => {
            for (const item of items) {
                uploadImage(item.file, item.id)
            }
        }, 0)
    }

    const tryPlayVideo = async (video: HTMLVideoElement, markReady: () => void) => {
        try {
            await video.play()
            markReady()
        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.debug('[capture] play() error (post-mount)', err)
            try {
                await new Promise((res) => setTimeout(res, 300))
                await video.play()
                markReady()
            } catch (finalErr) {
                if (process.env.NODE_ENV === 'development') console.debug('[capture] final play() error', finalErr)
                // Surface an error to the user so we don't silently swallow playback failures
                setCaptureError('Unable to start video playback')
            }
        }
    }

    const openLiveCapture = async () => {
        if (disabled || waitingForResponse) return
        if (!navigator.mediaDevices?.getUserMedia) {
            setCaptureError(t('chat:input.cameraNotSupported', { defaultValue: 'Camera API not supported in this browser' }))
            return
        }
        setCaptureError(null)
        setStreamReady(false)

        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop())
            streamRef.current = null
        }
        const constraintVariants: MediaStreamConstraints[] = [
            { video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false },
            { video: { facingMode: 'environment' }, audio: false },
            { video: true, audio: false }
        ]
        let obtained: MediaStream | null = null
        for (const c of constraintVariants) {
            try {
                obtained = await navigator.mediaDevices.getUserMedia(c)
                if (process.env.NODE_ENV === 'development') {
                    console.debug('[capture] obtained stream with constraints', c)
                }
                break
            } catch (e) {
                if (process.env.NODE_ENV === 'development') {
                    console.debug('[capture] constraint variant failed', c, e)
                }
            }
        }
        if (!obtained) {
            setCaptureError(t('chat:input.cameraAccessDenied', { defaultValue: 'Unable to access camera' }))
            return
        }
        const hasVideo = obtained.getVideoTracks().length > 0
        if (!hasVideo) {
            setCaptureError(t('chat:input.cameraNoVideo', { defaultValue: 'No video track available' }))
            obtained.getTracks().forEach(t => t.stop())
            return
        }
        streamRef.current = obtained
        setIsLiveCaptureOpen(true)
    }

    useEffect(() => {
        if (!isLiveCaptureOpen) return
        const stream = streamRef.current
        const video = videoRef.current
        if (!stream || !video) return
        video.srcObject = stream
        const markReady = () => { if (!streamReady) setStreamReady(true) }
        video.onloadedmetadata = () => {
            tryPlayVideo(video, markReady)
        }
        video.onplaying = markReady
        video.onloadeddata = () => { if (video.videoWidth > 0) markReady() }
        let attempts = 0
        const poll = () => {
            if (!isLiveCaptureOpen || streamReady) return
            attempts++
            if (video.videoWidth > 0) { markReady(); return }
            if (attempts < 30) {
                setTimeout(poll, 150)
            } else {
                setCaptureError(prev => prev || t('chat:input.cameraNoFrames', { defaultValue: 'Camera not delivering frames' }))
            }
        }
        const pollTimer = setTimeout(poll, 200)
        return () => clearTimeout(pollTimer)
    }, [isLiveCaptureOpen, streamReady, t])

    const closeLiveCapture = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop())
            streamRef.current = null
        }
        setIsLiveCaptureOpen(false)
        setStreamReady(false)
    }

    const takePhotoFromStream = () => {
        if (!videoRef.current || !streamReady) return
        const videoEl = videoRef.current
        const canvas = document.createElement('canvas')
        const width = videoEl.videoWidth || 1280
        const height = videoEl.videoHeight || 720
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        ctx.drawImage(videoEl, 0, 0, width, height)
        canvas.toBlob(blob => {
            if (!blob) {
                closeLiveCapture()
                setCaptureError(t('chat:input.captureFailedBlob', { defaultValue: 'Failed to capture image' }))
                return
            }

            const file = new File([blob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' })
            const id = `capture-${Date.now()}-${Math.random().toString(36).slice(2)}`
            setAttachments(prev => {
                if (prev.length >= maxFilesPerMessage) {
                    showAttachmentLimitError()
                    return prev
                }
                const item: AttachmentItem = { id, file, source: 'capture', previewUrl: URL.createObjectURL(file), url: undefined, loading: true }
                setTimeout(() => uploadImage(file, id), 0)
                return [...prev, item]
            })

            closeLiveCapture()
        }, 'image/jpeg', 0.9)
    }

    const closeOnboarding = () => {
        setOnboardingStep(null)
    }

    const handleOnboardingGotIt = () => {
        if (onboardingStep === 'camera') {
            setOnboardingStep('mic')
            return
        }
        if (onboardingStorageKey) {
            window.localStorage.setItem(onboardingStorageKey, '1')
        }
        setOnboardingStep(null)
    }

    const openUserManual = () => {
        const manualUrl = getUserManualUrl(i18n.language)
        window.open(manualUrl, '_blank', 'noopener,noreferrer')
    }

    const onboardingSourceElement = onboardingStep === 'camera' ? plusWrapperEl : micWrapperEl
    const showOnboarding =
        !!onboardingStep
        && onboardingEnabled
        && !isLiveCaptureOpen
        && (onboardingStep !== 'mic' || !text.length)
        && !!onboardingSourceElement

    return (
        <div className="input-area max-4-lines">
            <div className={`input-area-root${attachments.length > 0 ? ' -has-attachments' : ''}${filterSlot ? ' -has-filter' : ''}`}>
                <div className="left-slot">
                    {voice && !text.length && (
                        <InputAudio
                            {...audioInputProps}
                            assistant={assistant}
                            setQuestion={setQuestion}
                            validateTokenAndRehydrate={validateTokenAndRehydrate}
                            persistedData={persistedData}
                        />
                    )}
                    {!isAudioRecording && (
                        <div className="textfield-container">
                            {isTranscribingWithoutText && (
                                <div className='transcribing-overlay'>
                                    <ActivityIndicator size='small' />
                                </div>
                            )}
                            {!needsArea && (
                                <div className="audio-text-container">
                                    {attachments.length > 0 && (
                                        <div className="attachment-strip">
                                            {attachments.map(item => (
                                                <div className="attachment-item" key={item.id}>
                                                    <div className="attachment-preview">
                                                        {item.previewUrl && (
                                                            <img src={item.previewUrl} alt="attachment preview" />
                                                        )}
                                                        {item.loading && !item.error && (
                                                            <div className="thumb-loader-wrapper" aria-label="loading">
                                                                <ActivityIndicator size="small" />
                                                            </div>
                                                        )}
                                                        {item.error && (
                                                            <div className="thumb-error-wrapper" aria-label={item.error} role="alert">
                                                                <span aria-hidden="true">&#x26A0;</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="attachment-discard-btn"
                                                        aria-label={t('chat:input:discard')}
                                                        onClick={() => discardAttachment(item.id)}
                                                    >
                                                        <span aria-hidden="true">&#x2715;</span>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div className="text-row">
                                        {renderedFilterSlot}
                                        <TextField
                                            id="text"
                                            ref={textFieldRef}
                                            className="input-area-textfield"
                                            title={t('chat:yourPrompt')}
                                            // The browser's list of earlier entries must not fill the field again after a message is sent
                                            autoComplete="off"
                                            disabled={disabled || isTranscribingWithoutText}
                                            value={text}
                                            placeholder={showAttachmentNudge ? attachmentNudgeText : composerPlaceholderText}
                                            onChange={handleChangeTextField}
                                            onKeyDown={handlePromptKey}
                                            onFocus={handleOnFocus}
                                            onBlur={handleOnBlur}
                                        />
                                        {text.length > 0 && !disabled && !waitingForResponse && (
                                            <Button
                                                icon={'delete'}
                                                onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                                                onClick={() => updateText('')}
                                                mode='integrated'
                                            />
                                        )}
                                        {!disabled && !waitingForResponse && uploadActive && (
                                            <div className="inline-plus-wrapper" ref={setPlusWrapperEl}>
                                                <Popover
                                                    position="top-center"
                                                    strategy="fixed"
                                                    trigger={
                                                        <Button
                                                            mode="integrated"
                                                            icon={{ iconName: 'imagery-add', title: t('chat:input:add') }}
                                                        />
                                                    }
                                                    paragraph={
                                                        <div className="add-options-popover">
                                                            <div className="file-btn-wrapper">
                                                                <Button
                                                                    mode="tertiary"
                                                                    icon={{ iconName: 'camera', title: t('chat:input:capture') }}
                                                                    label={t('chat:input:capture')}
                                                                    onClick={openLiveCapture}
                                                                />
                                                            </div>
                                                            <div className="file-btn-wrapper">
                                                                <Button
                                                                    mode="tertiary"
                                                                    icon={{ iconName: 'image-gallery', title: t('chat:input:upload') }}
                                                                    label={t('chat:input:upload')}
                                                                    onClick={triggerUploadInput}
                                                                />
                                                                <input
                                                                    ref={uploadInputRef}
                                                                    type="file"
                                                                    accept={fileInputAccept}
                                                                    multiple
                                                                    className="file-overlay-input"
                                                                    onChange={handleUploadInputChange}
                                                                    aria-label={t('chat:input:upload')}
                                                                    hidden
                                                                />
                                                            </div>
                                                        </div>
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {needsArea && (
                                <>
                                    {renderedFilterSlot}
                                    <div className="audio-text-container">
                                        {attachments.length > 0 && (
                                            <div className="attachment-strip">
                                                {attachments.map(item => (
                                                    <div className="attachment-item" key={item.id}>
                                                        <div className="attachment-preview">
                                                            {item.previewUrl && (
                                                                <img src={item.previewUrl} alt="attachment preview" />
                                                            )}
                                                            {item.loading && !item.error && (
                                                                <div className="thumb-loader-wrapper" aria-label="loading">
                                                                    <ActivityIndicator size="small" />
                                                                </div>
                                                            )}
                                                            {!item.loading && item.error && (
                                                                <div className="thumb-error-wrapper" aria-label="upload failed" role="alert">
                                                                    <span aria-hidden="true">&#x26A0;</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="attachment-discard-btn"
                                                            aria-label={t('chat:input:discard')}
                                                            onClick={() => discardAttachment(item.id)}
                                                        >
                                                            <span aria-hidden="true">&#x2715;</span>
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <TextArea
                                            rows={8}
                                            id="text"
                                            ref={textAreaRef}
                                            className={`input-area-textarea${showAttachmentNudge ? ' -nudge-active' : ''}`}
                                            title={t('chat:yourPrompt')}
                                            autoComplete="off"
                                            disabled={disabled || waitingForResponse}
                                            value={text}
                                            placeholder={showAttachmentNudge ? attachmentNudgeText : composerPlaceholderText}
                                            dynamicHeight
                                            onChange={handleChangeTextArea}
                                            onKeyDown={handlePromptKey}
                                            onFocus={handleOnFocus}
                                            onBlur={handleOnBlur}
                                        />
                                    </div>
                                    <div className="textarea-actions">
                                        {!disabled && !waitingForResponse && uploadActive && (
                                            <div className="inline-plus-wrapper" ref={setPlusWrapperEl}>
                                                <Popover
                                                    position="top-center"
                                                    strategy="fixed"
                                                    trigger={
                                                        <Button
                                                            mode="integrated"
                                                            icon={{ iconName: 'imagery-add', title: t('chat:input:add') }}
                                                        />
                                                    }
                                                    paragraph={
                                                        <div className="add-options-popover">
                                                            <div className="file-btn-wrapper">
                                                                <Button
                                                                    mode="tertiary"
                                                                    icon={{ iconName: 'camera', title: t('chat:input:capture') }}
                                                                    label={t('chat:input:capture')}
                                                                    onClick={openLiveCapture}
                                                                />
                                                            </div>
                                                            <div className="file-btn-wrapper">
                                                                <Button
                                                                    mode="tertiary"
                                                                    icon={{ iconName: 'image-gallery', title: t('chat:input:upload') }}
                                                                    label={t('chat:input:upload')}
                                                                    onClick={triggerUploadInput}
                                                                />
                                                                <input
                                                                    ref={uploadInputRef}
                                                                    type="file"
                                                                    accept={fileInputAccept}
                                                                    multiple
                                                                    className="file-overlay-input"
                                                                    onChange={handleUploadInputChange}
                                                                    aria-label={t('chat:input:upload')}
                                                                    hidden
                                                                />
                                                            </div>
                                                        </div>
                                                    }
                                                />
                                            </div>
                                        )}
                                        {text.length > 0 && !disabled && !waitingForResponse && (
                                            <Button
                                                icon={'delete'}
                                                onClick={() => updateText('')}
                                                mode='integrated'
                                            />
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
                <div className="right-slot">
                    <div className="send-options-container">
                        {text.length ? (
                            <Button
                                className="send-button"
                                icon={{
                                    iconName: 'paperplane',
                                    title: t('chat:sendMessage'),
                                }}
                                onClick={handleSend}
                                disabled={text.length === 0 || waitingForResponse || attachments.some(a => a.loading)}
                            />
                        ) : (
                            <div className="audio-button-container" ref={setMicWrapperEl}>
                                <Button
                                    mode={"primary"}
                                    icon={{
                                        iconName: audioButtonSymbol,
                                        title: t(audioButtonSymbol === 'microphone-classic' ? 'chat:audio:record' : 'chat:audio:stop'),
                                    }}
                                    onClick={toggleRecording}
                                    disabled={audioButtonDisabled || isTranscribingWithoutText}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="answer-fade" />
            {showOnboarding && onboardingStep && (
                <FeatureOnboardingPopover
                    anchorEl={onboardingSourceElement}
                    step={onboardingStep}
                    onGotIt={handleOnboardingGotIt}
                    onClose={closeOnboarding}
                    onOpenManual={openUserManual}
                />
            )}
            {/* Inline attachment preview moved inside the input area; source notification removed */}
            {isLiveCaptureOpen && (
                <div className="live-capture-overlay">
                    <div className="live-capture-content">
                        <video
                            ref={videoRef}
                            playsInline
                            muted
                            autoPlay
                            className={`live-capture-video ${streamReady ? '-ready' : '-loading'}`}
                        />
                        {!streamReady && !captureError && (
                            <div className="live-capture-loading">{t('chat:input.loadingCamera')}</div>
                        )}
                        {captureError && (
                            <Notification type="error" className="capture-error">
                                {captureError}
                            </Notification>
                        )}
                        <div className="live-capture-actions">
                            <Button
                                mode="primary"
                                icon={{ iconName: 'camera', title: t('chat:input:capture') }}
                                label={t('chat:input:takePhoto')}
                                onClick={takePhotoFromStream}
                                disabled={!streamReady || !!captureError}
                            />
                            <Button
                                mode="secondary"
                                icon={{ iconName: 'close', title: t('general:buttons:cancel') }}
                                label={t('general:buttons:cancel')}
                                onClick={closeLiveCapture}
                            />
                        </div>
                    </div>
                </div>
            )}
            <div className='notification-wrapper'>
                {disabledMessage && disabled && (
                    <Notification className="notification" type="warning">
                        {disabledMessage}
                    </Notification>
                )}
                {attachmentLimitError && (
                    <Notification className="notification" type="warning">
                        {t('chat:input.attachmentLimitReached', { defaultValue: 'Maximum 10 images per message' })}
                    </Notification>
                )}
                {uploadError && (
                    <Notification className="notification" type="error">
                        {uploadError}
                    </Notification>
                )}
            </div>
        </div>
    )
}

export default InputArea
